import React, { useEffect, useMemo, useState } from "react";

// Customer data with club logos
const customers = [
  {
    name: "Old Leodiensians CC",
    logo: "https://snaptrix-content-generator-artefacts.s3.eu-north-1.amazonaws.com/club/205/1745953648485-OldLeodiensiansCC.png",
  },
  {
    name: "Headingley Bramhope CC",
    logo: "https://snaptrix-content-generator-artefacts.s3.eu-north-1.amazonaws.com/club/13694/1745953695577-Headingley_Bramhope_CC.png",
  },
  {
    name: "Muff Field CC",
    logo: "https://snaptrix-content-generator-artefacts.s3.eu-north-1.amazonaws.com/club/4468/1745953671677-Muff_Field_CC.png",
  },
  {
    name: "Green Hammerton CC",
    logo: "https://snaptrix-content-generator-artefacts.s3.eu-north-1.amazonaws.com/club/12097/1745953694836-Green_Hammerton_CC.png",
  },
  {
    name: "Sidbury CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/11509/1770581052531-logo.png",
  },
  {
    name: "Bracebridge CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/1309/1769026052885-logo.png",
  },
  {
    name: "Wavertree CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/6696/1770457715843-logo.png",
  },
  {
    name: "Armitage Bridge CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/754/1767382764530-logo.png",
  },
  {
    name: "Armitage CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/8694/1770246708158-logo.png",
  },
  {
    name: "The Burgs CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/11766/1772366052788-logo.png",
  },
  {
    name: "Bournville Village CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/10920/1772383656486-logo.png",
  },
  {
    name: "Adwalton CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/club/624/1767382651283-logo.png",
  },
  {
    name: "Eden Madras Parklands CC",
    logo: "https://snaptrix-content-generator-artefacts-eu-west-2.s3.eu-west-2.amazonaws.com/team/8073/156191/1776279176948-edenParklands-removebg-preview.png",
  },
];

export default function CustomersSection() {
  const [cardsPerView, setCardsPerView] = useState(2);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const updateCardsPerView = () => {
      if (window.innerWidth >= 1280) {
        setCardsPerView(5);
        return;
      }
      if (window.innerWidth >= 1024) {
        setCardsPerView(4);
        return;
      }
      if (window.innerWidth >= 768) {
        setCardsPerView(3);
        return;
      }
      if (window.innerWidth >= 480) {
        setCardsPerView(2);
        return;
      }
      setCardsPerView(1);
    };

    updateCardsPerView();
    window.addEventListener("resize", updateCardsPerView);

    return () => {
      window.removeEventListener("resize", updateCardsPerView);
    };
  }, []);

  const extendedCustomers = useMemo(
    () => [...customers, ...customers.slice(0, cardsPerView)],
    [cardsPerView]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);

    return () => {
      mediaQuery.removeEventListener("change", updatePreference);
    };
  }, []);

  useEffect(() => {
    if (customers.length <= cardsPerView || isPaused) {
      return undefined;
    }
    if (prefersReducedMotion) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }, 2800);

    return () => {
      clearInterval(intervalId);
    };
  }, [cardsPerView, isPaused, prefersReducedMotion]);

  useEffect(() => {
    if (currentIndex !== customers.length) {
      return undefined;
    }

    const resetTimeout = setTimeout(() => {
      setIsAnimating(false);
      setCurrentIndex(0);
    }, 500);

    return () => {
      clearTimeout(resetTimeout);
    };
  }, [currentIndex]);

  useEffect(() => {
    if (!isAnimating) {
      const frameId = window.requestAnimationFrame(() => {
        setIsAnimating(true);
      });

      return () => window.cancelAnimationFrame(frameId);
    }

    return undefined;
  }, [isAnimating]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [cardsPerView]);

  return (
    <section className="py-12 md:py-16 bg-gradient-to-b from-white via-snaptrix-gray-50 to-white relative overflow-hidden" aria-labelledby="customers-heading">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-1/4 w-48 h-48 bg-snaptrix-blue-100 rounded-full blur-3xl opacity-30 -mt-24" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-100 rounded-full blur-3xl opacity-30 -mb-32" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <p
          id="customers-heading"
          className="text-sm md:text-base font-medium text-snaptrix-gray-500 text-center mb-10 uppercase tracking-wide"
        >
          Trusted by Cricket Clubs Across the UK
        </p>
        <div
          className="overflow-hidden"
          tabIndex={0}
          aria-label="Trusted clubs logo carousel"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocus={() => setIsPaused(true)}
          onBlur={() => setIsPaused(false)}
        >
          <div
            className={`flex ${isAnimating ? "transition-transform duration-500 ease-out" : ""}`}
            style={{
              transform: `translateX(-${(currentIndex * 100) / cardsPerView}%)`,
            }}
          >
            {extendedCustomers.map((customer, index) => (
              <div
                key={`${customer.name}-${index}`}
                className="flex-shrink-0 px-3"
                style={{ width: `${100 / cardsPerView}%` }}
              >
                <div className="flex flex-col items-center justify-center group relative">
                  <div className="w-[100px] h-[100px] rounded-full overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-110 border-2 border-snaptrix-gray-100 mb-3 bg-white">
                    <img
                      src={customer.logo}
                      alt={`${customer.name} logo`}
                      className="w-full h-full object-cover transition-all duration-300"
                      title={customer.name}
                      loading="lazy"
                    />
                  </div>
                  <span className="text-base md:text-lg font-medium text-snaptrix-gray-700 text-center max-w-[170px] leading-tight">
                    {customer.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
